// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createEntityAdapter } from '@reduxjs/toolkit';
import type {
  AdvisoryPositionsState,
  AdvisoryBalancesHistoricalState,
  AdvisoryTransactionsState,
  AdvisoryAccountsDebtState,
  AdvisoryRepositoryState,
} from '@shared/data-access/interfaces/redux/advisory-repository-state-types';

/**
 * Helper function to generate cache key for advisory positions Format:
 * `${currency}@${client_id}:${iban}`
 */
export function generateAdvisoryPositionsCacheKey(
  currency: string,
  clientId: number,
  iban: string,
): string {
  return `${currency}@${clientId}:${iban}`;
}

/**
 * Helper function to generate cache key for advisory balances historical Format:
 * `${currency}@${client_id}:${iban}:${start_date}:${end_date}`
 */
export function generateAdvisoryBalancesHistoricalCacheKey(
  currency: string,
  clientId: number,
  iban: string,
  startDate: string,
  endDate: string,
): string {
  return `${currency}@${clientId}:${iban}:${startDate}:${endDate}`;
}

/**
 * Helper function to generate cache key for advisory transactions Format:
 * `${currency}@${client_id}:${iban}:${start_date}`
 */
export function generateAdvisoryTransactionsCacheKey(
  currency: string,
  clientId: number,
  iban: string,
  startDate: string,
): string {
  return `${currency}@${clientId}:${iban}:${startDate}`;
}

/**
 * Helper function to generate cache key for advisory accounts debt Format:
 * `${currency}@${client_id}:${iban}:${start_date}:${end_date}`
 */
export function generateAdvisoryAccountsDebtCacheKey(
  currency: string,
  clientId: number,
  iban: string,
  startDate: string,
  endDate: string,
): string {
  return `${currency}@${clientId}:${iban}:${startDate}:${endDate}`;
}

export const advisoryPositionsAdapter = createEntityAdapter<
  AdvisoryPositionsState,
  string
>({
  selectId: ({ currency, client_id, iban }) =>
    generateAdvisoryPositionsCacheKey(currency, client_id, iban),
  sortComparer: (a, b) => {
    const keyA = generateAdvisoryPositionsCacheKey(a.currency, a.client_id, a.iban);
    const keyB = generateAdvisoryPositionsCacheKey(b.currency, b.client_id, b.iban);
    return keyA.localeCompare(keyB);
  },
});

export const advisoryBalancesHistoricalAdapter = createEntityAdapter<
  AdvisoryBalancesHistoricalState,
  string
>({
  selectId: ({ currency, client_id, iban, start_date, end_date }) =>
    generateAdvisoryBalancesHistoricalCacheKey(
      currency,
      client_id,
      iban,
      start_date,
      end_date,
    ),
  sortComparer: (a, b) => {
    const keyA = generateAdvisoryBalancesHistoricalCacheKey(
      a.currency,
      a.client_id,
      a.iban,
      a.start_date,
      a.end_date,
    );
    const keyB = generateAdvisoryBalancesHistoricalCacheKey(
      b.currency,
      b.client_id,
      b.iban,
      b.start_date,
      b.end_date,
    );
    return keyA.localeCompare(keyB);
  },
});

export const advisoryTransactionsAdapter = createEntityAdapter<
  AdvisoryTransactionsState,
  string
>({
  selectId: ({ currency, client_id, iban, start_date }) =>
    generateAdvisoryTransactionsCacheKey(currency, client_id, iban, start_date),
  sortComparer: (a, b) => {
    const keyA = generateAdvisoryTransactionsCacheKey(
      a.currency,
      a.client_id,
      a.iban,
      a.start_date,
    );
    const keyB = generateAdvisoryTransactionsCacheKey(
      b.currency,
      b.client_id,
      b.iban,
      b.start_date,
    );
    return keyA.localeCompare(keyB);
  },
});

export const advisoryAccountsDebtAdapter = createEntityAdapter<
  AdvisoryAccountsDebtState,
  string
>({
  selectId: ({ currency, client_id, iban, start_date, end_date }) =>
    generateAdvisoryAccountsDebtCacheKey(
      currency,
      client_id,
      iban,
      start_date,
      end_date,
    ),
  sortComparer: (a, b) => {
    const keyA = generateAdvisoryAccountsDebtCacheKey(
      a.currency,
      a.client_id,
      a.iban,
      a.start_date,
      a.end_date,
    );
    const keyB = generateAdvisoryAccountsDebtCacheKey(
      b.currency,
      b.client_id,
      b.iban,
      b.start_date,
      b.end_date,
    );
    return keyA.localeCompare(keyB);
  },
});

export const initialState: AdvisoryRepositoryState = {
  positions: advisoryPositionsAdapter.getInitialState(),
  balancesHistorical: advisoryBalancesHistoricalAdapter.getInitialState(),
  transactions: advisoryTransactionsAdapter.getInitialState(),
  accountsDebt: advisoryAccountsDebtAdapter.getInitialState(),
};
